/**
 * Skupiny claimu a klasifikace rozporu (Milnik 1, bod 6).
 *
 * NALEZ 2. 9. 2026: zalozka Konflikty hlasila „zadny konflikt", presto Lysa
 * nese pro `parcel.area.subject_total_m2` dve hodnoty — 2 078 m2 (soucet dle
 * KN/INSPIRE) a 2 048 m2 (rucni poznamka na plánku). OBE maji evidenci
 * (INSPIRE dotaz, resp. RAW_INPUT plánek). Konflikt se ztratil az v projekci:
 * `dedupeFacts` slucoval podle SAMOTNEHO `factKey` a z nekolika hodnot nechal
 * jen tu s nejvyssim trust bandem — rozporna hodnota zmizela bez stopy.
 *
 * Pravidla, ktera tenhle modul drzi:
 *  1) Deduplikovat lze JEN skutecne ekvivalentni claimy: stejny scope
 *     (factKey + nodeId) A stejna normalizovana hodnota.
 *  2) Ruzne hodnoty ve stejnem scope se NIKDY neslucuji.
 *  3) Detekce rozporu bezi nad NEDEDUPLIKOVANOU sadou.
 *  4) Fakt bez evidence se nesmi zobrazit jako potvrzeny fakt. Kdyz ale
 *     odporuje jine hodnote, nesmi se ani ztratit — dostane stav
 *     UNVERIFIED_CONTRADICTORY_INPUT a UI ho zobrazi jako neovereny vstup.
 *
 * Zadna evidence se tu nevyraba ani nedomysli; modul jen cte, co je ve fixture.
 */
/**
 * Stav jedne hodnoty vuci ostatnim hodnotam tehoz scope.
 *
 * - `NONE` — ve scope je jen tahle hodnota (nebo jen jeji duplikaty).
 * - `CONFLICT` — ve scope jsou nejmene dve ruzne hodnoty a tahle ma evidenci.
 * - `UNVERIFIED_CONTRADICTORY_INPUT` — hodnota odporuje jine, ale sama nema
 *   evidenci. Neni to potvrzeny fakt, ale ani se nesmi zahodit.
 * - `NEEDS_REVALIDATION` — hodnota ma evidenci, odporuje jine a je prokazatelne
 *   STARSI (obe strany maji `sourceDate`). Nejde nutne o spor, ale o zastarani.
 */
export const CLAIM_CONFLICT_STATES = [
    'NONE',
    'CONFLICT',
    'UNVERIFIED_CONTRADICTORY_INPUT',
    'NEEDS_REVALIDATION',
];
/**
 * Kanonicky tvar hodnoty pro POROVNANI. Klice objektu se radi, aby na poradi
 * nezalezelo. Pouziva se jen na rovnost, nikdy se nezobrazuje.
 */
export function normalizeFactValue(value) {
    const walk = (input) => {
        if (Array.isArray(input))
            return input.map(walk);
        if (typeof input === 'object' && input !== null) {
            const record = input;
            const out = {};
            for (const key of Object.keys(record).sort())
                out[key] = walk(record[key]);
            return out;
        }
        return input;
    };
    return JSON.stringify(walk(value) ?? null);
}
/** Scope claimu: stejna vec u stejneho uzlu. Bez nodeId je scope samotny factKey. */
export function factScopeKey(fact) {
    return `${fact.factKey}@${fact.nodeId ?? '-'}`;
}
function hasEvidence(fact) {
    return fact.evidenceIds.length > 0;
}
/** Milisekundy `sourceDate`, nebo null kdyz datum chybi/neni platne. */
function sourceTime(fact) {
    if (fact.sourceDate === null)
        return null;
    const ms = Date.parse(fact.sourceDate);
    return Number.isNaN(ms) ? null : ms;
}
/**
 * Rozdeli fakta do scope skupin, v kazde slouci ekvivalentni hodnoty a
 * zbylym priradi stav rozporu. Vstup je NEDEDUPLIKOVANY seznam faktu.
 */
export function classifyClaimGroups(facts) {
    const byScope = new Map();
    const order = [];
    for (const fact of facts) {
        const key = factScopeKey(fact);
        const bucket = byScope.get(key);
        if (bucket)
            bucket.push(fact);
        else {
            byScope.set(key, [fact]);
            order.push(key);
        }
    }
    return order.map((scopeKey) => {
        const group = byScope.get(scopeKey) ?? [];
        // Ekvivalentni hodnoty (stejny scope + stejna hodnota) -> jeden claim,
        // ktery nese dukazy vsech svych duplikatu.
        const byValue = new Map();
        const valueOrder = [];
        for (const fact of group) {
            const value = normalizeFactValue(fact.value);
            const bucket = byValue.get(value);
            if (bucket)
                bucket.push(fact);
            else {
                byValue.set(value, [fact]);
                valueOrder.push(value);
            }
        }
        const merged = valueOrder.map((value) => {
            const duplicates = byValue.get(value) ?? [];
            const first = duplicates[0];
            if (duplicates.length === 1)
                return first;
            const evidenceIds = [...new Set(duplicates.flatMap((f) => f.evidenceIds))];
            // Zastupce = ten s nejvic dukazy; ostatni pole zustavaji jeho.
            const best = duplicates.reduce((a, b) => (b.evidenceIds.length > a.evidenceIds.length ? b : a));
            return { ...best, evidenceIds, independentSources: evidenceIds.length };
        });
        const distinctValues = merged.length;
        const newestOther = (self) => {
            const times = merged.filter((f) => f.id !== self.id).map(sourceTime).filter((t) => t !== null);
            return times.length === 0 ? null : Math.max(...times);
        };
        const classified = merged.map((fact) => {
            if (distinctValues < 2)
                return { ...fact, conflict: 'NONE' };
            if (!hasEvidence(fact))
                return { ...fact, conflict: 'UNVERIFIED_CONTRADICTORY_INPUT' };
            const own = sourceTime(fact);
            const other = newestOther(fact);
            if (own !== null && other !== null && own < other) {
                return { ...fact, conflict: 'NEEDS_REVALIDATION' };
            }
            return { ...fact, conflict: 'CONFLICT' };
        });
        return { scopeKey, factKey: classified[0]?.factKey ?? '', distinctValues, facts: classified };
    });
}
/**
 * Fakta pro zobrazeni. Oproti puvodnimu `visibleFacts` + `dedupeFacts`:
 * projdou fakta s evidenci A navic rozporne vstupy bez evidence (ty nesou
 * stav UNVERIFIED_CONTRADICTORY_INPUT a UI je NESMI vydavat za potvrzeny fakt).
 * Fakt bez evidence, ktery nicemu neodporuje, se dal nezobrazuje.
 */
export function displayFacts(facts) {
    return classifyClaimGroups(facts)
        .flatMap((g) => g.facts)
        .filter((f) => f.evidenceIds.length > 0 || f.conflict === 'UNVERIFIED_CONTRADICTORY_INPUT');
}
/**
 * Kolik faktu se NEZOBRAZI kvuli chybejici evidenci. Pocitaji se jen fakta bez
 * evidence, ktera zaroven nicemu neodporuji — slouceny duplikat (stejna hodnota
 * stejneho scope) neni skryty fakt, jen se vykresli jednim radkem.
 */
export function hiddenFactCount(facts) {
    const groups = classifyClaimGroups(facts);
    const distinctByScope = new Map(groups.map((g) => [g.scopeKey, g.distinctValues]));
    return facts.filter((f) => f.evidenceIds.length === 0 && (distinctByScope.get(factScopeKey(f)) ?? 1) < 2).length;
}
/** Skupiny s vice nez jednou hodnotou — vstup zalozky Konflikty. */
export function conflictGroups(facts) {
    return classifyClaimGroups(facts).filter((g) => g.distinctValues > 1);
}
//# sourceMappingURL=claims.js.map